import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Search, CheckCircle, Save, Calendar, Lock, FileText } from "lucide-react";

const OverweightPage = () => {
  const [data, setData] = useState([]);
  const [prices, setPrices] = useState({});
  const [activeTab, setActiveTab] = useState("EXPORT");
  const [searchTerm, setSearchTerm] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(""); // Tanggal invoice wajib dipilih


  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get("/api/overweights");
      setData(res.data);
    } catch (error) { console.error(error); }
  };

  const handlePriceChange = (id, val) => {
    const rawValue = val.replace(/[^0-9]/g, "");
    setPrices({ ...prices, [id]: rawValue });
  };

  const handleSavePrice = async (id) => {
    if (!prices[id]) return toast.error("Isi harga dulu!");
    try {
      await axios.put(`/api/overweights/${id}/price`, { price: parseFloat(prices[id]) });
      toast.success("Harga tersimpan!");
      fetchData();
    } catch (e) { toast.error("Gagal simpan"); }
  };

  const handleApprove = async (id) => {
    if (!invoiceDate) return toast.error("Pilih tanggal invoice dulu!");
    try {
      const res = await axios.post(`/api/overweights/${id}/approve`, {
        invoice_date: invoiceDate
      });
      toast.success(res.data.message || "Berhasil! Masuk ke Invoice History.");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal approve"); }
  };
  const handleSendToDraft = async (id) => {
    if (!invoiceDate) return toast.error("Pilih tanggal invoice dulu!");
    try {
      const res = await axios.post(`/api/overweights/${id}/send-to-draft`, {
        invoice_date: invoiceDate
      });
      toast.success(res.data.message || "Berhasil dikirim ke Draft.");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal kirim ke draft"); }
  };

  const formatIDR = (val) => {
    if (!val) return "";
    return parseInt(val, 10).toLocaleString('id-ID');
  };

  // FILTERING DATA (By Tab & Search)
  const filteredData = data.filter(item => {
    const matchTab = (item.category || "EXPORT") === activeTab;
    const searchLower = searchTerm.toLowerCase();
    const matchSearch = 
        item.awb?.toLowerCase().includes(searchLower) || 
        item.customer_name?.toLowerCase().includes(searchLower) || 
        item.destination?.toLowerCase().includes(searchLower);
    return matchTab && matchSearch;
  });

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">

      {/* HEADER */}
      <div className="bg-white p-6 rounded-xl border shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 text-red-600">
                <Package /> Overweight Manager ({">"} 30Kg)
            </h1>
            <p className="text-gray-500">Input harga manual lalu Approve untuk masuk ke History.</p>
        </div>
        
        {/* TAB EXPORT / IMPORT / DOMESTIC */}
        <div className="flex bg-gray-100 p-1 rounded-lg font-bold text-sm">
            {["EXPORT", "IMPORT", "DOMESTIC"].map(tab => (
                <button 
                    key={tab} 
                    onClick={() => setActiveTab(tab)}
                    className={`px-6 py-2 rounded-md transition ${activeTab === tab ? 'bg-white text-red-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    {tab}
                </button>
            ))}
        </div>
      </div>

      {/* INPUT TANGGAL INVOICE - WAJIB DIISI SEBELUM APPROVE */}
      <div className={`p-5 rounded-xl border-2 shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4 transition-colors ${!invoiceDate ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
          <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${!invoiceDate ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                  {!invoiceDate ? <Lock size={22} /> : <Calendar size={22} />}
              </div>
              <div>
                  <h3 className={`font-bold text-base ${!invoiceDate ? 'text-red-800' : 'text-green-800'}`}>
                      Tanggal Invoice (Wajib Dipilih)
                  </h3>
                  <p className={`text-xs mt-0.5 ${!invoiceDate ? 'text-red-600' : 'text-green-700'}`}>
                      {!invoiceDate 
                          ? "Pilih tanggal invoice sebelum bisa Approve overweight." 
                          : `Tombol Approve aktif — invoice akan dibuat per ${new Date(invoiceDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`
                      }
                  </p>
              </div>
          </div>
          <input 
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className={`p-2.5 border-2 rounded-xl text-sm font-bold outline-none transition cursor-pointer bg-white ${!invoiceDate ? 'border-red-300 focus:border-red-500' : 'border-green-300 focus:border-green-500'}`}
          />
      </div>

      {/* SEARCH BOX */}
      <div className="bg-white p-4 border rounded-xl shadow-sm relative">
        <Search className="absolute left-7 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input 
            type="text" 
            placeholder="Cari AWB, Customer, atau Destinasi..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
        />
      </div>

      {/* TABEL */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-red-50 border-b text-red-800">
            <tr>
              <th className="px-6 py-4">AWB</th>
              <th className="px-6 py-4">Customer</th>
              <th className="px-6 py-4">Dest</th>
              <th className="px-6 py-4 text-center">Weight</th>
              <th className="px-6 py-4 w-64">Input Harga Total (Rp)</th>
              <th className="px-6 py-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredData.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-8 text-gray-400">Tidak ada data overweight di kategori ini.</td></tr>
            ) : (
                filteredData.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-bold">{item.awb}</td>
                        <td className="px-6 py-4">
                            <div className="font-medium">{item.customer_name}</div>
                            <div className="text-xs text-gray-500">{item.customer_id}</div>
                            {item.department && (
                                <div className="text-xs text-blue-600 font-bold mt-0.5">{item.department}</div>
                            )}
                        </td>
                        <td className="px-6 py-4">{item.destination}</td>
                        <td className="px-6 py-4 text-center font-bold text-red-600">{item.weight_kg} Kg</td>
                        <td className="px-6 py-4">
                            <div className="flex gap-2 items-center">
<span className="font-bold text-gray-500">Rp</span>
                                <input 
                                    type="text" 
                                    className="border rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-red-500 outline-none text-right font-mono font-bold"
                                    placeholder={item.manual_total_price ? formatIDR(item.manual_total_price) : "0"}
                                    value={prices[item.id] !== undefined ? formatIDR(prices[item.id]) : ""}
                                    onChange={(e) => handlePriceChange(item.id, e.target.value)}
                                />
                                <button onClick={() => handleSavePrice(item.id)} className="bg-gray-800 hover:bg-black text-white p-2.5 rounded-lg transition">
                                    <Save size={16}/>
                                </button>
                            </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                            <div className="flex gap-2 justify-end">
                            <button 
                                onClick={() => handleSendToDraft(item.id)}
                                disabled={(!item.manual_total_price && !prices[item.id]) || !invoiceDate}
                                title={!invoiceDate ? "Pilih tanggal invoice dulu!" : "Kirim sebagai draft, akan tergabung otomatis dengan invoice bulanan customer ini saat digenerate"}
                                className={`px-3 py-2 rounded-lg font-bold text-white flex items-center gap-2 transition text-sm ${
                                    !invoiceDate
                                        ? 'bg-gray-300 cursor-not-allowed opacity-60'
                                        : item.manual_total_price > 0
                                            ? 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200'
                                            : 'bg-gray-300 cursor-not-allowed'
                                }`}
                            >
                                {!invoiceDate ? <Lock size={16}/> : <FileText size={16}/>}
                                Kirim ke Draft
                            </button>
                            <button 
                                onClick={() => handleApprove(item.id)}
                                disabled={(!item.manual_total_price && !prices[item.id]) || !invoiceDate}
                                title={!invoiceDate ? "Pilih tanggal invoice dulu!" : "Langsung terbitkan invoice baru/gabung ke invoice yang sudah published"}
                                className={`px-4 py-2 rounded-lg font-bold text-white flex items-center gap-2 transition ${
                                    !invoiceDate 
                                        ? 'bg-gray-300 cursor-not-allowed opacity-60' 
                                        : item.manual_total_price > 0 
                                            ? 'bg-green-600 hover:bg-green-700 shadow-lg shadow-green-200' 
                                            : 'bg-gray-300 cursor-not-allowed'
                                }`}
                            >
                                {!invoiceDate ? <Lock size={16}/> : <CheckCircle size={16}/>}
                                {!invoiceDate ? 'Pilih Tanggal' : 'Approve'}
                            </button>
                            </div>
                        </td>
                    </tr>
                ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OverweightPage;